import { IndicationSet, RolodexIndication } from 'features/indicationTriage'
import { StepValidationResult } from 'features/libra/workflow/workflowStepsApi'
import { GeckoComponent } from 'types'

export interface ComputeFfsTriageValidationArgs {
  componentId: string | undefined
  inspectionId: string | undefined
  component: GeckoComponent | undefined
  ltaSet: IndicationSet | undefined
  indications: ReadonlyArray<RolodexIndication>
  isLoading: boolean
}

export interface ComputeFfsTriageValidationResult {
  validation: StepValidationResult
  totalCount: number
  triagedCount: number
}

/**
 * Pure validation for the FFS indication-triage step. Tier ladder order
 * matters: synchronous prerequisite checks come before `isLoading`, and
 * `isLoading` comes before any data-derived check that could lie during a
 * normal in-flight fetch.
 */
export function computeFfsTriageValidation({
  componentId,
  inspectionId,
  component,
  ltaSet,
  indications,
  isLoading,
}: ComputeFfsTriageValidationArgs): ComputeFfsTriageValidationResult {
  const triagedCount = indications.filter((ind) =>
    Boolean(ind.classificationInfo?.classification)
  ).length
  const totalCount = indications.length

  let validation: StepValidationResult = undefined

  if (!inspectionId || !componentId) {
    validation = {
      status: 'Error',
      message: 'Ticket is missing required component or inspection tag.',
    }
  } else if (isLoading) {
    validation = {
      status: 'Warning',
      message: 'Loading indication data...',
    }
  } else if (!component) {
    validation = {
      status: 'Error',
      message: 'Component entry not found in Rolodex.',
    }
  } else if (!ltaSet) {
    validation = {
      status: 'Warning',
      message:
        'No LTA indication set found for this (component, inspection) yet. Generate one upstream before triage.',
    }
  } else if (totalCount === 0) {
    validation = {
      status: 'Warning',
      message:
        'Indication set exists but has no indications -- re-run LTA generation if expected.',
    }
  } else if (triagedCount < totalCount) {
    validation = {
      status: 'Error',
      message: `${totalCount - triagedCount} of ${totalCount} indication${totalCount === 1 ? '' : 's'} still need to be classified.`,
    }
  }

  return { validation, totalCount, triagedCount }
}
